import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { ChatProvider, ChatWidget, LoadingState, UnifiedNavbar, Footer } from '@lykbl/ui';
import { DevModeProvider, DevModeIndicator, DevModeAuthGate } from '@lykbl/dev-tools';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useFoundationAvailability } from '@lykbl/foundation';
import { supabase } from './lib/supabaseClient';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const CoLab = lazy(() => import('./pages/CoLab'));
const CampaignConnect = lazy(() => import('./pages/CampaignConnect'));
const Creators = lazy(() => import('./pages/Creators'));
const Brands = lazy(() => import('./pages/Brands'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Help = lazy(() => import('./pages/Help'));
const About = lazy(() => import('./pages/About'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Foundation signup & waitlist
const JoinCreator = lazy(() => import('./pages/JoinCreator'));
const Waitlist = lazy(() => import('./pages/Waitlist'));
const BrandWaitlist = lazy(() => import('./pages/BrandWaitlist'));
const CES2026 = lazy(() => import('./pages/CES2026'));

// Legal pages
const Terms = lazy(() => import('./pages/legal/Terms'));
const Privacy = lazy(() => import('./pages/legal/Privacy'));

// Auth pages
const Auth = lazy(() => import('./pages/auth/Auth'));
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'));

// Main layout with unified Navbar and Footer
function MainLayout() {
  const { isAuthenticated, user, role, signOut } = useAuth();
  const { status } = useFoundationAvailability(supabase, 'creator');

  // Until seats are full, route CTAs to Foundation v2.
  // Once full, route to the normal auth signup flow.
  const primaryCtaHref = status === 'full' ? '/auth?signup=true' : '/join-creator';
  
  return (
    <>
      <a href="#main" className="skip-link">Skip to main content</a>
      <UnifiedNavbar
        site="marketing"
        isAuthenticated={isAuthenticated}
        user={user ? { email: user.email, role: role ?? undefined } : null}
        onSignOut={signOut}
        authLink="/auth"
        primaryCtaHref={primaryCtaHref}
      />
      <main id="main" className="min-h-screen">
        <Outlet />
      </main>
      <Footer site="marketing" />
    </>
  );
}

// Auth layout without Navbar/Footer
function AuthLayout() {
  return (
    <main className="min-h-screen">
      <Outlet />
    </main>
  );
}

function App() {
  // Get chat API URL from environment
  const chatApiUrl = import.meta.env.VITE_CHAT_API_URL || '/functions/v1/chat';
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

  return (
    <BrowserRouter>
      <DevModeProvider appType="marketing">
        <DevModeAuthGate>
          <AuthProvider>
            <ChatProvider mode="marketing" chatApiUrl={chatApiUrl} supabaseAnonKey={supabaseAnonKey}>
              <Suspense fallback={<LoadingState text="Loading..." fullScreen />}>
                <Routes>
                  {/* Auth routes - no navbar/footer */}
                  <Route element={<AuthLayout />}>
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                  </Route>

                  {/* Main routes with navbar/footer */}
                  <Route element={<MainLayout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/colab" element={<CoLab />} />
                    <Route path="/campaign-connect" element={<CampaignConnect />} />
                    <Route path="/creators" element={<Creators />} />
                    <Route path="/brands" element={<Brands />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:id" element={<BlogPost />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/about" element={<About />} />
                    
                    {/* Foundation signup & waitlist */}
                    <Route path="/join-creator" element={<JoinCreator />} />
                    <Route path="/waitlist" element={<Waitlist />} />
                    <Route path="/brands/waitlist" element={<BrandWaitlist />} />
                    <Route path="/CES2026" element={<CES2026 />} />
                    
                    {/* Legal */}
                    <Route path="/legal/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/legal/privacy" element={<Navigate to="/privacy" replace />} />
                    <Route path="/ai-amp" element={<Navigate to="https://lykbl.ai" replace />} />
                    
                    {/* Legacy footer links (avoid 404s from older deployments / cached builds) */}
                    <Route path="/careers" element={<Navigate to="/about" replace />} />
                    <Route path="/press" element={<Navigate to="/blog" replace />} />
                    <Route path="/press-kit" element={<Navigate to="/blog" replace />} />
                    <Route path="/contact" element={<Navigate to="/help" replace />} />
                    
                    {/* 404 */}
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
              </Suspense>
              
              {/* Global chat widget */}
              <ChatWidget />
              
              {/* Dev mode indicator (only shows on localhost/staging) */}
              <DevModeIndicator />
            </ChatProvider>
          </AuthProvider>
        </DevModeAuthGate>
      </DevModeProvider>
    </BrowserRouter>
  );
}

export default App;

